import { useState, useMemo, useCallback } from "react";
import {
  Add,
  ArrowCircleDown,
  ArrowCircleUp,
  Clear,
  ColorLens,
  Delete,
  Gradient,
  Image,
} from "@mui/icons-material";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Icon,
  IconButton,
  InputAdornment,
  MenuItem,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Property,
  PropertyComposite,
  PropertyRadio,
  PropertySelect,
  PropertySlider,
  PropertyStack,
  Sector,
} from "grapesjs";
import { useEditor } from "@grapesjs/react";
import { iconMap } from "features/lightningLanders/constants";
import LightningLanderFlexField from "features/lightningLanders/components/LightningLanderFlexField";
import DimensionFields from "features/lightningLanders/components/DimensionFields";
import BoxModelEditor from "features/lightningLanders/components/BoxModelEditor";
import { colorVariableToCustomTheme } from "features/lightningLanders/utils";
import { useLightningLanderThemeSettings } from "features/lightningLanders/providers/LightningLanderThemeProvider";
import ColorPicker from "react-best-gradient-color-picker";
import CustomDialog from "Components/CustomDialog/CustomDialog";
import DebouncedTextField from "features/common/components/DebouncedTextField";

interface ILightningLandersStyleFieldsProps extends React.HTMLProps<HTMLDivElement> {
  prop: Property;
  sector: Sector;
}

function LightningLandersStyleFields({
  prop,
  sector,
}: ILightningLandersStyleFieldsProps) {
  const editor = useEditor();
  const theme = useTheme();
  const { theme: customTheme } = useLightningLanderThemeSettings();
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const attributes: any = prop.attributes || {};
  const { units = ["px", "%"] } = attributes; // Extract possible units

  // Get current value and unit from prop
  const getCurrentValue = useCallback(() => {
    return prop.getValue() || "";
  }, [prop]);

  const getCurrentUnit = useCallback(() => {
    return (prop as any).getUnit?.() || units[0] || "px";
  }, [prop, units]);

  // Get margin and padding properties for box model editor
  const getMarginPaddingProps = () => {
    if (!editor) return { marginProp: null, paddingProp: null };

    const selected = editor.getSelected();
    if (!selected) return { marginProp: null, paddingProp: null };

    const styleManager = editor.StyleManager;
    const sectors = styleManager.getSectors();

    let marginProp: Property | null = null;
    let paddingProp: Property | null = null;

    sectors.forEach((sector) => {
      sector.getProperties().forEach((property) => {
        if (property.get("property") === "margin") {
          marginProp = property;
        }
        if (property.get("property") === "padding") {
          paddingProp = property;
        }
      });
    });

    return { marginProp, paddingProp };
  };

  const handleValueChange = useCallback(
    (newValue: string) => {
      if (
        prop.getName() === "width" ||
        prop.getName() === "height" ||
        prop.getValue() === newValue
      ) {
        return;
      }

      // Update the property immediately
      if (newValue === "") {
        prop.clear();
      } else {
        const currentUnit = getCurrentUnit();
        if ((prop.attributes as any).units) {
          prop.upValue(`${newValue}${currentUnit}`);
        } else {
          prop.upValue(newValue);
        }
      }
    },
    [prop, getCurrentUnit],
  );

  const handleUnitChange = useCallback(
    (newUnit: string) => {
      const currentValue = getCurrentValue();
      if (currentValue === "") {
        // Don't update if value is empty
        return;
      }
      prop.upValue(`${currentValue}${newUnit}`);
    },
    [prop, getCurrentValue],
  );

  const openAssets = useCallback(() => {
    const { Assets } = editor;
    Assets.open({
      select: (asset, complete) => {
        prop.upValue(asset.getSrc());
        complete && Assets.close();
      },
      types: ["image"],
      accept: "image/*",
    });
  }, [editor, prop]);

  const onChange = useCallback(
    (ev: any) => {
      handleValueChange(ev.target.value);
    },
    [handleValueChange],
  );

  const isWidthField = prop.get("property") === "width";
  const isHeightField = prop.get("property") === "height";
  const isMarginField = prop.get("property") === "margin";
  const isPaddingField = prop.get("property") === "padding";

  const renderField = useMemo(() => {
    // Check if this is a margin field and if we should render the box model editor
    const { marginProp, paddingProp } = getMarginPaddingProps();

    // If this is a margin field and we have both margin and padding props, render the box model editor
    if (isMarginField && marginProp && paddingProp) {
      return (
        <BoxModelEditor
          marginProp={marginProp as any}
          paddingProp={paddingProp as any}
        />
      );
    }

    // If this is a padding field and we have both props, don't render anything (handled by margin field)
    if (isPaddingField && marginProp && paddingProp) {
      return <></>;
    }

    const currentValue = getCurrentValue();
    const currentUnit = getCurrentUnit();

    let inputToRender = (
      <DebouncedTextField
        label={prop.getLabel()}
        value={currentValue}
        onChange={handleValueChange}
        size="small"
        fullWidth
        type={
          prop.getType() === "number" || prop.getType() === "integer"
            ? "number"
            : "text"
        }
        debounceMs={300}
        sx={
          units.length > 0
            ? {
                "& .MuiInputBase-root": { paddingRight: 0.5 },
                "& .MuiFormLabel-root": { maxWidth: "calc(100% - 57px)" },
              }
            : undefined
        }
        InputProps={
          units.length > 0
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <DebouncedTextField
                      select
                      value={currentUnit}
                      onChange={handleUnitChange}
                      size="small"
                      variant="standard"
                      debounceMs={0}
                      sx={{
                        width: 40,
                        "& .MuiSelect-select": {
                          padding: 0,
                          textAlign: "center",
                        },
                      }}
                    >
                      {units.map((unitOption) => (
                        <MenuItem key={unitOption} value={unitOption}>
                          {unitOption}
                        </MenuItem>
                      ))}
                    </DebouncedTextField>
                  </InputAdornment>
                ),
              }
            : undefined
        }
      />
    );

    switch (prop.getType()) {
      case "radio": {
        const radioProp = prop as PropertyRadio;

        if ((radioProp as any).attributes.ui === "toggle") {
          inputToRender = (
            <>
              <Typography color="secondary" gutterBottom>
                {prop.getLabel()}
              </Typography>
              <ToggleButtonGroup
                fullWidth
                size="small"
                color="primary"
                exclusive // Ensure only one value is selected
                value={currentValue}
                onChange={(event, newValue) => {
                  if (newValue !== null) {
                    handleValueChange(newValue); // Update value and trigger re-render
                  }
                }}
              >
                {radioProp.getOptions().map((option) => {
                  const label = radioProp.getOptionLabel(option);
                  const description =
                    (option as any).tooltip ?? (option as any).get?.("tooltip");
                  const tooltipTitle = description
                    ? `${label} - ${description}`
                    : label;
                  return (
                    <Tooltip
                      key={radioProp.getOptionId(option)}
                      arrow
                      title={tooltipTitle}
                    >
                      <ToggleButton
                        size="small"
                        value={radioProp.getOptionId(option)}
                        key={radioProp.getOptionId(option)}
                        sx={{ flex: 1 }}
                      >
                        <>
                          {Boolean((option as any).icon) &&
                          iconMap[(option as any).icon] ? (
                            <Icon
                              component={iconMap[(option as any).icon]}
                              fontSize="small"
                            />
                          ) : (
                            radioProp.getOptionLabel(option)
                          )}
                        </>
                      </ToggleButton>
                    </Tooltip>
                  );
                })}
              </ToggleButtonGroup>
              {radioProp.id === "flex" &&
                !["1 1 100%", "auto", "0 1 auto"].includes(
                  getCurrentValue(),
                ) && (
                  <LightningLanderFlexField
                    flexValue={prop.getValue() || "0 1 auto"}
                    handleFlexChange={(newFlexValue: string) => {
                      prop.upValue(newFlexValue);
                    }}
                  />
                )}
            </>
          );
        } else {
          // todo: change backgrou in to toggle io
          if (radioProp.id === "__background-type") {
            inputToRender = (
              <ToggleButtonGroup
                fullWidth
                color="primary"
                exclusive // Ensure only one value is selected
                value={currentValue}
                onChange={(event, newValue) => {
                  if (newValue !== null) {
                    handleValueChange(newValue); // Update value and trigger re-render
                  }
                }}
              >
                {radioProp.getOptions().map((option) => (
                  <ToggleButton
                    value={radioProp.getOptionId(option)}
                    key={radioProp.getOptionId(option)}
                    sx={{ flex: 1 }}
                  >
                    {radioProp.getOptionId(option) === "image" ? (
                      <Image />
                    ) : radioProp.getOptionId(option) === "color" ? (
                      <ColorLens />
                    ) : (
                      <Gradient />
                    )}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            );
          } else {
            inputToRender = (
              <FormControl>
                <FormLabel>{prop.getLabel()}</FormLabel>
                <RadioGroup value={currentValue} onChange={onChange} row>
                  {radioProp.getOptions().map((option) => (
                    <FormControlLabel
                      key={radioProp.getOptionId(option)}
                      value={radioProp.getOptionId(option)}
                      label={radioProp.getOptionLabel(option)}
                      control={<Radio size="small" />}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            );
          }
        }

        break;
      }

      case "select": {
        const selectProp = prop as PropertySelect;
        inputToRender = (
          <TextField
            select
            label={prop.getLabel()}
            placeholder={prop.getDefaultValue()}
            value={currentValue}
            onChange={onChange}
            size="small"
            fullWidth
          >
            {selectProp.getOptions().map((option) => (
              <MenuItem
                key={selectProp.getOptionId(option)}
                value={selectProp.getOptionId(option)}
              >
                {selectProp.getOptionLabel(option)}
              </MenuItem>
            ))}
          </TextField>
        );
        break;
      }

      case "color": {
        inputToRender = (
          <>
            <Stack
              p={2}
              spacing={1}
              sx={{
                width: "100%",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
              }}
            >
              <TextField
                fullWidth
                label={prop.getLabel()}
                placeholder={prop.getDefaultValue()}
                value={currentValue}
                onChange={onChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Tooltip title="Select Color">
                        <Box
                          sx={{
                            backgroundColor:
                              colorVariableToCustomTheme(currentValue) ||
                              currentValue,
                            border: `1px solid ${theme.palette.primary.main}`,
                            borderRadius: theme.shape.borderRadius,
                            width: 25,
                            height: 25,
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            setColorPickerOpen(true);
                          }}
                        ></Box>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
              <Typography color="secondary">Theme Colors</Typography>
              <Stack direction="row" spacing={1} mt={1}>
                <Tooltip title="Primary">
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      background: customTheme.primaryColor,
                      border: "1px solid #ccc",
                      cursor: "pointer",
                    }}
                    onClick={() => handleValueChange("var(--primary-color)")}
                  />
                </Tooltip>
                <Tooltip title="Secondary">
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      background: customTheme.secondaryColor,
                      border: "1px solid #ccc",
                      cursor: "pointer",
                    }}
                    onClick={() => handleValueChange("var(--secondary-color)")}
                  />
                </Tooltip>
                <Tooltip title="Text">
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "50%",
                      background: customTheme.textColor,
                      border: "1px solid #ccc",
                      cursor: "pointer",
                    }}
                    onClick={() => handleValueChange("var(--text-color)")}
                  />
                </Tooltip>
              </Stack>
            </Stack>
            {colorPickerOpen && (
              <CustomDialog
                open={colorPickerOpen}
                onClose={() => setColorPickerOpen(false)}
              >
                <DialogContent>
                  <ColorPicker
                    value={currentValue}
                    onChange={(value) => handleValueChange(value)}
                    disableDarkMode
                    hidePresets
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setColorPickerOpen(false)}>
                    Close
                  </Button>
                </DialogActions>
              </CustomDialog>
            )}
          </>
        );
        break;
      }

      case "slider": {
        const sliderProp = prop as PropertySlider;
        inputToRender = (
          <>
            <Typography color="secondary">{prop.getLabel()}</Typography>
            <Slider
              size="small"
              value={parseFloat(currentValue)}
              min={sliderProp.getMin()}
              max={sliderProp.getMax()}
              step={sliderProp.getStep()}
              onChange={onChange}
              valueLabelDisplay="auto"
            />
          </>
        );
        break;
      }

      case "file": {
        inputToRender = (
          <>
            <Typography color="secondary">{prop.getLabel()}</Typography>
            <Stack direction={"row"} spacing={2} alignItems="center" mt={1}>
              <Box
                sx={{
                  height: 40,
                  width: 40,
                  borderRadius: theme.shape.borderRadius,
                  backgroundSize: "cover",
                  backgroundImage: currentValue.includes("url(")
                    ? currentValue.replaceAll(`'`, "")
                    : `url("${currentValue.replaceAll(`'`, "")}")`,
                  border: `1px solid ${theme.palette.primary.main}`,
                }}
              />

              <Stack>
                <Button type="button" onClick={openAssets}>
                  Select Image
                </Button>
              </Stack>
              <IconButton onClick={() => handleValueChange("")}>
                <Delete />
              </IconButton>
            </Stack>
          </>
        );
        break;
      }
      case "composite":
        {
          const compositeProp = prop as PropertyComposite;
          inputToRender = (
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <Typography color="secondary">{prop.getLabel()}</Typography>
              </Grid>
              {compositeProp.getProperties().map((p) => (
                <LightningLandersStyleFields
                  key={p.getId()}
                  prop={p}
                  sector={sector}
                />
              ))}
            </Grid>
          );
        }
        break;
      case "stack": {
        const stackProp = prop as PropertyStack;
        const isTextShadow = stackProp.getName() === "text-shadow";
        inputToRender = (
          <div>
            <Stack
              direction={"row"}
              justifyContent="space-between"
              alignItems={"center"}
            >
              <Typography color="secondary">{prop.getLabel()}</Typography>
              <Stack
                direction={"row"}
                justifyContent="space-between"
                alignItems={"center"}
              >
                {prop.canClear() && (
                  <IconButton
                    onClick={() => {
                      prop.clear();
                    }}
                  >
                    <Clear />
                  </IconButton>
                )}

                <IconButton
                  size="small"
                  onClick={() => {
                    (prop as PropertyStack).addLayer({}, { at: 0 });
                  }}
                >
                  <Add />
                </IconButton>
              </Stack>
            </Stack>

            {(prop as PropertyStack).layers.map((layer, i) => (
              <div key={i}>
                <Stack direction="row">
                  <IconButton
                    size="small"
                    onClick={() => {
                      layer.move(layer.getIndex() - 1);
                    }}
                  >
                    <ArrowCircleUp />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      layer.move(layer.getIndex() + 1);
                    }}
                  >
                    <ArrowCircleDown />
                  </IconButton>
                  <Button
                    sx={{ flexGrow: 1 }}
                    onClick={() => {
                      layer.select();
                    }}
                  >
                    {layer.getLabel()}
                  </Button>
                  <Box
                    ml={1}
                    sx={{
                      background: "white",
                      minWidth: 17,
                      minHeight: 17,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: 4,
                      ...layer.getStylePreview({
                        number: { min: -3, max: 3 },
                        camelCase: true,
                      }),
                    }}
                  >
                    {isTextShadow && "T"}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => {
                      layer.remove();
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Stack>
                {layer.isSelected() && (
                  <Box
                    my={2}
                    py={4}
                    sx={{
                      borderTop: `1px solid ${theme.palette.divider2}`,
                    }}
                  >
                    <Grid container spacing={1.5}>
                      {stackProp.getProperties().map((p) => (
                        <LightningLandersStyleFields
                          key={p.getId()}
                          prop={p}
                          sector={sector}
                        />
                      ))}
                    </Grid>
                  </Box>
                )}
              </div>
            ))}
          </div>
        );
        break;
      }
    }

    return inputToRender;
  }, [
    getCurrentValue,
    getCurrentUnit,
    handleValueChange,
    handleUnitChange,
    onChange,
    prop,
    theme,
    units,
    openAssets,
    customTheme,
    isWidthField,
    getMarginPaddingProps,
    isMarginField,
    isPaddingField,
    editor?.getSelected()?.getId(), // Add selected component ID as dependency
  ]);

  if (!prop.isVisible() || isHeightField || isPaddingField) {
    return <></>;
  }

  // If this is a height field and we have both props, don't render anything (handled by width field)
  if (isHeightField) {
    return <></>;
  }

  if (isWidthField) {
    return (
      <Grid item xs={12}>
        <DimensionFields sector={sector} />
      </Grid>
    );
  }

  return (
    <Grid
      item
      xs={
        prop.isFull() ||
        prop.getType() === "color" ||
        isWidthField ||
        isMarginField
          ? 12
          : 6
      }
    >
      <div>{renderField}</div>
    </Grid>
  );
}

export default LightningLandersStyleFields;
